import { useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LayoutDashboard, Plus, ListChecks, CreditCard, LogOut, Menu, X, Zap, ImageIcon, DollarSign } from 'lucide-react';
import { Button } from '../components/ui/button';
import { formatCredits } from '../utils/credits';

const NAV_ITEMS = [
  { path: '/dashboard', label: 'Overview', icon: LayoutDashboard, exact: true },
  { path: '/dashboard/submit', label: 'New Job', icon: Plus },
  { path: '/dashboard/jobs', label: 'Queue', icon: ListChecks },
  { path: '/dashboard/results', label: 'Results', icon: ImageIcon },
  { path: '/pricing', label: 'Credits', icon: CreditCard },
];

export default function Dashboard() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isActive = (path, exact) => exact ? location.pathname === path : location.pathname.startsWith(path);

  return (
    <div className="min-h-screen flex bg-[#FAFAFA]">
      <aside className="hidden lg:flex flex-col w-60 shrink-0 bg-white border-r border-gray-100" data-testid="dashboard-sidebar">
        <div className="px-6 py-5 border-b border-gray-100">
          <Link to="/" className="flex items-baseline gap-0" data-testid="sidebar-logo">
            <span className="font-body text-xl font-extrabold tracking-tight text-[#1A1A2E] uppercase">DIS</span>
            <span className="font-body text-xl font-extrabold tracking-tight uppercase text-[#FF6B4A]">2</span>
            <span className="font-body text-xl font-extrabold tracking-tight text-[#1A1A2E] uppercase">ORT</span>
          </Link>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-0.5">
          {NAV_ITEMS.map((item) => (
            <Link key={item.path} to={item.path} data-testid={`sidebar-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-colors ${isActive(item.path, item.exact) ? 'bg-[#FFF0EA] text-[#FF6B4A] font-semibold' : 'text-[#8A8A9A] hover:text-[#1A1A2E] hover:bg-gray-50'}`}>
              <item.icon className="w-4 h-4" />
              {item.label}
            </Link>
          ))}
        </nav>
        <div className="px-4 pb-4 border-t border-gray-100 pt-4 space-y-3">
          <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#FFF0EA]" data-testid="sidebar-credits">
            <Zap className="w-4 h-4 text-[#FF6B4A]" />
            <span className="font-mono text-sm font-bold text-[#FF6B4A]">{formatCredits(user, { decimals: 1 })}</span>
            <span className="text-xs text-[#8A8A9A]">credits</span>
          </div>
          <div className="flex items-center gap-3 px-4 py-2">
            {user?.picture ? <img src={user.picture} alt="" className="w-8 h-8 rounded-full" /> : <div className="w-8 h-8 rounded-full bg-[#FFF0EA] flex items-center justify-center text-xs font-bold text-[#FF6B4A]">{user?.name?.[0]}</div>}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-[#1A1A2E] truncate">{user?.name}</div>
              <div className="text-[10px] text-[#8A8A9A] truncate">{user?.email}</div>
            </div>
          </div>
          <Button onClick={logout} variant="ghost" className="w-full justify-start text-[#8A8A9A] hover:text-[#EF4444] text-xs h-9 px-4 rounded-xl" data-testid="sidebar-logout"><LogOut className="w-4 h-4 mr-2" /> Sign Out</Button>
        </div>
      </aside>

      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 h-14 bg-white border-b border-gray-100 flex items-center justify-between px-4 shadow-sm">
        <Link to="/" className="font-body text-lg font-extrabold text-[#1A1A2E] uppercase tracking-tight"><span>DIS</span><span className="text-[#FF6B4A]">2</span><span>ORT</span></Link>
        <div className="flex items-center gap-3">
          <span className="font-mono text-xs font-bold text-[#FF6B4A]"><Zap className="w-3 h-3 inline" /> {formatCredits(user, { decimals: 1 })}</span>
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-[#1A1A2E]" data-testid="mobile-menu-toggle">{sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}</button>
        </div>
      </div>

      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/20" onClick={() => setSidebarOpen(false)} />
          <div className="absolute left-0 top-14 bottom-0 w-60 bg-white border-r border-gray-100 p-3 space-y-0.5">
            {NAV_ITEMS.map((item) => (
              <Link key={item.path} to={item.path} onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm ${isActive(item.path, item.exact) ? 'bg-[#FFF0EA] text-[#FF6B4A] font-semibold' : 'text-[#8A8A9A]'}`}>
                <item.icon className="w-4 h-4" /> {item.label}
              </Link>
            ))}
            <button onClick={() => { logout(); setSidebarOpen(false); }} className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#EF4444] w-full"><LogOut className="w-4 h-4" /> Sign Out</button>
          </div>
        </div>
      )}

      <main className="flex-1 min-w-0 lg:h-screen lg:overflow-y-auto pt-14 lg:pt-0">
        <div className="p-6 lg:p-10 max-w-5xl"><Outlet /></div>
      </main>
    </div>
  );
}
